import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Phone, Zap, CheckCircle2 } from "lucide-react";
import { NEIGHBORHOODS } from "../data/neighborhoods";
import { SERVICES_DATA } from "../data/services";
import { FAQ, STEPS } from "../data/faq";
import { TEL_LINK, waLinkFor, PHONE_FORMATTED } from "../constants/contact";
import { Seo } from "../components/Seo";
import { OpenStatus } from "../components/OpenStatus";
import { Footer } from "../components/Footer";
import { FadeIn } from "../components/FadeIn";
import { WhatsAppIcon } from "../components/WhatsAppIcon";

export default function NeighborhoodPage() {
  const { slug } = useParams();
  const n = NEIGHBORHOODS.find((x) => x.slug === slug);
  if (!n) return <Navigate to="/" replace />;

  const faqLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  });

  return (
    <>
      <Seo
        title={`${n.short} Elektrikçi | 7/24 Usta Servis — Beylikdüzü`}
        description={n.meta}
      />
      <Helmet>
        <script type="application/ld+json">{faqLd}</script>
      </Helmet>

      <section data-testid="neighborhood-page" className="relative pt-32 sm:pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-grid" aria-hidden="true" />
        <div
          aria-hidden="true"
          className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-amber-500/12 blur-[130px]"
        />
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500 mb-5" data-testid="neighborhood-breadcrumb">
              <Link to="/" className="hover:text-amber-400 transition-colors">Beylikdüzü Elektrikçi</Link>
              <span className="mx-2 text-slate-700">/</span>
              <span className="text-amber-400">{n.name}</span>
            </p>
            <div className="mb-6">
              <OpenStatus />
            </div>
            <h1
              data-testid="neighborhood-title"
              className="font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-slate-50"
            >
              {n.short} <span className="text-amber-400">Elektrikçi</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
              {n.desc}
            </p>
            <p className="mt-4 max-w-2xl text-base text-slate-500 leading-relaxed" data-testid="neighborhood-extra">
              {n.extra}
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a
                href={TEL_LINK}
                data-testid="neighborhood-call-button"
                className="inline-flex items-center justify-center gap-3 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-display font-bold text-base sm:text-lg px-8 py-4 transition-colors duration-200 shadow-[0_0_36px_rgba(245,158,11,0.4)]"
              >
                <Phone className="w-5 h-5" strokeWidth={2.5} />
                HEMEN ARA: {PHONE_FORMATTED}
              </a>
              <a
                href={waLinkFor(n.name)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="neighborhood-whatsapp-button"
                className="inline-flex items-center justify-center gap-3 rounded-full bg-green-500 hover:bg-green-400 text-black font-display font-bold text-base sm:text-lg px-8 py-4 transition-colors duration-200"
              >
                <WhatsAppIcon className="w-5 h-5" />
                WHATSAPP İLE YAZ
              </a>
            </div>
          </motion.div>

          <FadeIn delay={0.1} className="mt-16">
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-slate-50 mb-6">
              {n.name}'nde yaptığımız işler
            </h2>
            <p className="mb-6 max-w-2xl text-sm sm:text-base text-slate-400 leading-relaxed">
              Aşağıdaki tüm hizmetleri {n.name} ve çevresinde aynı gün servis anlayışıyla
              sunuyoruz. Detay için hizmetin üzerine dokunabilirsiniz.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="neighborhood-services">
              {SERVICES_DATA.map((s, i) => (
                <Link
                  key={s.slug}
                  to={`/hizmet/${s.slug}`}
                  data-testid={`neighborhood-service-${i}`}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-800 bg-[#0F172A] px-5 py-4 hover:border-amber-500/40 transition-colors"
                >
                  <CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0" strokeWidth={2.25} />
                  <span className="text-slate-200 font-medium text-sm sm:text-base group-hover:text-amber-300 transition-colors">
                    {s.title}
                  </span>
                </Link>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={0.12} className="mt-16">
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-slate-50 mb-6">
              Nasıl çalışıyoruz?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="neighborhood-steps">
              {STEPS.map(({ icon: Icon, title, desc }, i) => (
                <div
                  key={title}
                  data-testid={`neighborhood-step-${i}`}
                  className="rounded-2xl border border-slate-800 bg-[#0F172A] p-6"
                >
                  <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 mb-4">
                    <Icon className="w-5 h-5" strokeWidth={2.25} />
                  </span>
                  <h3 className="font-display font-semibold text-slate-50">{title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={0.15} className="mt-16">
            <div className="rounded-3xl border border-amber-500/30 bg-amber-500/5 p-7 sm:p-9 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <h2 className="font-display font-bold tracking-tight text-xl sm:text-2xl text-slate-50">
                  {n.short}'da arızanız mı var?
                </h2>
                <p className="mt-2 text-sm sm:text-base text-slate-400">
                  Arayın, bugün gelelim. Telefonu doğrudan usta açar.
                </p>
              </div>
              <a
                href={TEL_LINK}
                data-testid="neighborhood-cta-call"
                className="inline-flex shrink-0 items-center gap-3 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-display font-bold px-7 py-3.5 transition-colors duration-200"
              >
                <Phone className="w-4 h-4" strokeWidth={2.5} />
                {PHONE_FORMATTED}
              </a>
            </div>
          </FadeIn>

          <FadeIn delay={0.18} className="mt-16">
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-slate-50 mb-6">
              Sık sorulan sorular
            </h2>
            <div className="space-y-4" data-testid="neighborhood-faq">
              {FAQ.map((f, i) => (
                <div
                  key={i}
                  data-testid={`neighborhood-faq-${i}`}
                  className="rounded-2xl border border-slate-800 bg-[#0F172A] p-6"
                >
                  <h3 className="font-display font-semibold text-slate-50 text-base sm:text-lg">
                    {f.q}
                  </h3>
                  <p className="mt-2 text-sm sm:text-base text-slate-400 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={0.2} className="mt-16">
            <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-5">
              Diğer hizmet bölgelerimiz
            </h2>
            <div className="flex flex-wrap gap-x-6 gap-y-3" data-testid="neighborhood-other-links">
              {NEIGHBORHOODS.filter((x) => x.slug !== slug).map((x) => (
                <Link
                  key={x.slug}
                  to={`/mahalle/${x.slug}`}
                  data-testid={`neighborhood-link-${x.slug}`}
                  className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-400 transition-colors"
                >
                  <Zap className="w-3 h-3 text-amber-500/60" fill="currentColor" />
                  {x.short} Elektrikçi
                </Link>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>
      <Footer />
    </>
  );
}
